<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { reloadOnContentChanged } from '@/composables/useContentRefresh';

const { t } = useI18n();

// Types
interface OtherPathway {
  id: number;
  title: string;
  url: string;
  icon_url: string | null;
  moderation_state?: string;
  collection_id: number | null;
  collection_title: string | null;
  collection_url: string | null;
  area_id: number | null;
}

// Props from drupalSettings
const settings = (window as any).drupalSettings?.epbOtherPathwaysBlock || {};

const nodeId = ref<number>(settings.nodeId || 0);
const currentPathwayId = ref<number | null>(settings.currentPathwayId || null);
const otherPathways = ref<OtherPathway[]>(settings.otherPathways || []);

// Computed
const hasPathways = computed(() => otherPathways.value.length > 0);

// Navigate to pathway
function selectPathway(pathway: OtherPathway) {
  window.location.href = pathway.url;
}

// Rendered from drupalSettings (no re-fetch endpoint): reflect changes made by
// another navigation block with a deferred page reload.
reloadOnContentChanged('otherpathwaysblock');
</script>

<template>
  <div v-if="hasPathways" class="other-pathways-container">
    <div class="other-pathway-title">
      <i class="bi bi-signpost-split"></i>
      <span>{{ t('epb.other_pathways.title', 'Branch to another Pathway') }}</span>
    </div>

    <ul class="item-list other_pathway">
      <li
        v-for="pathway in otherPathways"
        :key="pathway.id"
      >
        <div class="other-pathway-text">
          <div class="popover-trigger">
            <span v-if="pathway.icon_url" class="other-pathway-image">
              <img :src="pathway.icon_url" alt="" class="pathway-icon-img">
            </span>
            <span v-else class="other-pathway-image">
              <i class="bi bi-signpost-2"></i>
            </span>
            <a
              :href="pathway.url"
              class="other-pathway-link"
              :class="pathway.moderation_state ? `moderation_state-${pathway.moderation_state}` : ''"
              @click.prevent="selectPathway(pathway)"
            >
              {{ pathway.title }}
            </a>
          </div>
          <div v-if="pathway.collection_title" class="other-pathway-collection">
            <a
              v-if="pathway.collection_url"
              :href="pathway.collection_url"
              class="other-pathway-collection-link"
              :title="t('epb.other_pathways.switch_collection', 'Switch to this collection (same content)')"
            >
              <i class="bi bi-collection"></i>
              {{ pathway.collection_title }}
            </a>
            <span v-else>
              <i class="bi bi-collection"></i>
              {{ pathway.collection_title }}
            </span>
          </div>
        </div>
      </li>
    </ul>

    <hr class="horizontal-divider hd-bottom">
  </div>
</template>

<style scoped>
.other-pathways-container {
  background-color: var(--bs-white);
  margin: 0;
  padding: 0;
}

.other-pathway-title {
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  font-weight: bold;
  border-bottom: 1px solid var(--bs-gray-200);
}

.item-list.other_pathway {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.other-pathway-text {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.5rem;
  gap: 0.5rem;
  align-items: flex-start;
  justify-content: space-between;
  box-shadow: 0 6px 8px -6px rgba(0, 0, 0, 0.25);
  background-color: var(--bs-gray-100);
  border: 2px dashed var(--bs-gray-200);
  border-left: 4px solid #0d6efd;
  border-radius: 8px;
}

.other-pathway-link {
  text-decoration: none;
  font-weight: 600;
}

.other-pathway-image {
  display: flex;
  align-items: center;
}

.other-pathway-image img {
  /* Size determined by Drupal image style */
}

.other-pathway-image i {
  color: var(--bs-secondary);
  font-size: 1.2rem;
}

.other-pathway-collection {
  font-size: smaller;
  align-self: flex-end;
  color: var(--bs-gray-600);
}

.other-pathway-collection-link {
  color: var(--bs-gray-600);
  text-decoration: none;
}

.other-pathway-collection-link:hover {
  color: var(--bs-primary);
  text-decoration: underline;
}

.popover-trigger {
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  text-decoration: none;
  border: none;
}

.pathway-icon-img {
  /* Size determined by Drupal image style (icon_48) */
}

/* Spinning animation for signpost icon */
@keyframes spinY {
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
}

.other-pathway-text .bi.bi-signpost-2 {
  transform-origin: center center;
  backface-visibility: visible;
  animation: spinY 12s linear infinite;
}

.horizontal-divider.hd-bottom {
  margin-top: 1rem;
  margin-bottom: 0;
  border-top: 1px solid var(--bs-gray-300);
}
</style>
